import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  RelatorioIncluirInput,
  RelatorioIncluirSchema,
} from "../RelatorioSistema.Dto";
import {
  RelatorioIncluir,
  RelatorioAtualizar,
  RelatorioFiltrar,
  RelatorioExcluir,
  RelatorioBaixar,
} from "src/services/Relatorio.Service";
import { showToast } from "src/componentes";
import { useEffect } from "react";
import { MapearErrosServidor } from "src/hook/Functions";
import { useMessageBox } from "src/Contexto/MessageBox.Context";

const RelatorioSistemaCadastroCode = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const { ShowMessage } = useMessageBox();

  const form = useForm<RelatorioIncluirInput>({
    resolver: yupResolver(RelatorioIncluirSchema),
    defaultValues: {
      modulo: 0,
      subModulo: 0,
    },
  });

  useEffect(() => {
    if (id) {
      setLoading(true);
      RelatorioFiltrar({ id: Number(id) })
        .then((response) => {
          if (response.data.success && response.data.data.length > 0) {
            const data = response.data.data[0];
            form.reset({
              nome: data.nome,
              modulo: data.modulo,
              subModulo: data.subModulo,
              arquivo: data.arquivo, // Base64
            });
          } else {
            MapearErrosServidor(response.data.notifications, showToast);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [id]);

  const onSubmit = async (data: RelatorioIncluirInput) => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        modulo: Number(data.modulo),
        subModulo: Number(data.subModulo),
      };

      if (id) {
        (payload as any).id = Number(id);
      }

      const response = id
        ? await RelatorioAtualizar(payload)
        : await RelatorioIncluir(payload);

      if (response.data.success) {
        showToast({
          message: id
            ? "Relatório atualizado com sucesso"
            : "Relatório incluído com sucesso",
          variant: "Success",
        });
        navigate("/relatorio/sistema");
      } else {
        showToast({
          message: id ? "Erro ao atualizar relatório" : "Erro ao incluir relatório",
          variant: "Danger",
        });
      }
    } catch (error) {
      console.error(error);
      showToast({ message: "Ocorreu erro interno", variant: "Danger" });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        const base64 = base64String.split(",")[1];
        form.setValue("arquivo", base64);
        if (!form.getValues("nome")) {
          form.setValue("nome", file.name);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleExcluir = async () => {
    if (!id) return;

    ShowMessage(
      "Deseja realmente excluir este relatório?",
      "Confirmação",
      "YesNo",
      "Question",
      {
        onYes: async () => {
          try {
            const response = await RelatorioExcluir(Number(id));
            if (response.data.success) {
              showToast({
                message: "Relatório excluído com sucesso",
                variant: "Success",
              });
              navigate("/relatorio/sistema");
            } else {
              MapearErrosServidor(response.data.notifications, showToast);
            }
          } catch (error) {
            console.error(error);
          }
        },
      }
    );
  };

  const handleBaixar = async () => {
    if (!id) return;
    try {
      const response = await RelatorioBaixar(Number(id));
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", form.getValues("nome") || "relatorio.frx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      showToast({ message: "Erro ao baixar arquivo", variant: "Danger" });
      console.error(error);
    }
  };

  return {
    loading,
    form,
    onSubmit,
    handleFileChange,
    handleExcluir,
    handleBaixar,
    id,
  };
};

export default RelatorioSistemaCadastroCode;
